import { useCallback, useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { Block as BlockIcon } from '@mui/icons-material';
import CustomBackdrop from './CustomBackdrop';
import './PolicyViewerPdf.css';
import CloseIconSvg from '../../assets/icons/closeIcon.svg';
import useCommonMediaQuery from '../../hooks/useCommonMediaQuery';
import EllipsisTooltip from './EllipsisTooltip';

export default function PolicyViewerPdf({ open, onClose, policy, fileUrl }) {
  const isNarrowViewport = useCommonMediaQuery('md');
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  // Handle iframe error
  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const getIframeUrl = useCallback(url => {
    if (!url) return '';
    if (url.includes('drive.google.com') && url.includes('/file/d/')) {
      const fileId = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
      if (fileId) {
        return `https://drive.google.com/file/d/${fileId[1]}/preview`;
      }
    }
    return url;
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [open]);

  // Handle modal close
  const handleClose = () => {
    onClose();
  };

  // Remove file extension from filename
  const trimExtension = filename => {
    if (!filename) return '';
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return filename;
    return filename.substring(0, lastDotIndex);
  };

  return (
    <>
      <CustomBackdrop
        open={open}
        modalDialogSelector=".policy-viewer-modal, .MuiDialog-root"
      />
      <Dialog
        open={open}
        fullScreen={isNarrowViewport}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') handleClose();
        }}
        maxWidth={isNarrowViewport ? false : 'lg'}
        fullWidth={!isNarrowViewport}
        hideBackdrop
        disableScrollLock
        disableEnforceFocus
        className="policy-viewer-modal"
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10002,
            ...(isNarrowViewport
              ? {
                  height: '100%',
                  maxHeight: '100%',
                  m: 0,
                  borderRadius: 0,
                  pb: 'env(safe-area-inset-bottom, 0px)',
                }
              : {
                  height: '90vh',
                  maxHeight: 'min(90vh, 900px)',
                  borderRadius: 2,
                  m: 2,
                }),
          },
        }}
        sx={{
          zIndex: 10002,
          '& .MuiDialog-container': {
            alignItems: { xs: 'stretch', md: 'center' },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            flexShrink: 0,
            borderBottom: '1px solid #e0e0e0',
            pt: {
              xs: 'calc(10px + env(safe-area-inset-top, 0px))',
              md: 2,
            },
            pb: { xs: 1.25, md: 2 },
            px: { xs: 1.5, md: 2 },
          }}
        >
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              pr: 1,
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            <EllipsisTooltip
              title={trimExtension(policy?.fileName) || 'Policy Document'}
            >
              {trimExtension(policy?.fileName) || 'Policy Document'}
            </EllipsisTooltip>
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close"
            sx={{
              p: 0.5,
              flexShrink: 0,
              mt: { xs: -0.25, md: 0 },
            }}
          >
            <Box
              component="img"
              src={CloseIconSvg}
              alt=""
              sx={{
                width: { xs: 28, md: 35 },
                height: { xs: 28, md: 35 },
                display: 'block',
              }}
            />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {hasError ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: 'min(70dvh, 400px)', md: 400 },
                p: { xs: 2, md: 3 },
                px: { xs: 2.5, md: 3 },
              }}
            >
              <BlockIcon
                sx={{
                  fontSize: { xs: 40, md: 48 },
                  color: '#f44336',
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                color="error"
                sx={{
                  mb: 1,
                  textAlign: 'center',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              >
                Unable to Load Document
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  maxWidth: 360,
                  lineHeight: 1.5,
                }}
              >
                The policy document could not be loaded. This may be due to
                security restrictions or an invalid link.
              </Typography>
            </Box>
          ) : (
            <Box
              className="iframe-container"
              sx={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
              }}
            >
              <iframe
                ref={iframeRef}
                src={getIframeUrl(fileUrl)}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                title={`Policy Document: ${trimExtension(policy?.fileName) || 'Unknown'}`}
              />
              {isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5',
                    px: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center' }}
                  >
                    Loading policy document...
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
